import type { Editor } from "@tiptap/core";
import Image from "@tiptap/extension-image";

/** A picture in the reply's text (the pictures-in-the-text round, 2026-10-29; brief rule 1, the red team's findings 1, 5 and 25): tiptap's
 *  image node, a block of its own, selectable, deletable with Backspace and movable by cut and paste, drawn at most the column's width. It
 *  carries the uploaded file's address (`src`), its id (`data-attachment`) and its name (`alt`); the server turns it into the stored marker.
 *  Only pictures this composer uploaded itself become nodes: a pasted web page's or mail's own pictures (an outside address, another reply's
 *  file) are dropped when the paste is read, so nothing outside loads. While its upload runs, a picture shows the local file and has no id
 *  yet (`pending`); Send waits for it. */
export type PastedPicture = { id: string; url: string; name: string };
export const PASTE_PICTURE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

export function composerPicture(own: { has: (id: string) => boolean }) {
  return Image.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        attachment: { default: null, parseHTML: (el: HTMLElement) => el.getAttribute("data-attachment"), renderHTML: (a: { attachment: string | null }) => (a.attachment ? { "data-attachment": a.attachment } : {}) },
        pending: { default: null, rendered: false },
      };
    },
    parseHTML() {
      return [{ tag: "img[data-attachment]", getAttrs: (el: HTMLElement) => (own.has(el.getAttribute("data-attachment") ?? "") ? null : false) }];
    },
    addInputRules() { return []; }, // no "![words](address)": a picture enters by paste only
    renderText({ node }) { return `[Picture: ${String(node.attrs.alt ?? "")}]`; },
  }).configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "ob-composer__picture" } });
}

/** a pasted picture at the cursor: shown at once from the local file, given its address and id when the upload answers, taken out when it fails */
export async function insertPastedPicture(editor: Editor, file: File, upload: (file: File) => Promise<PastedPicture | null>, pending: { add: () => void; done: () => void }): Promise<void> {
  const key = `p${Math.random().toString(36).slice(2)}`;
  const local = URL.createObjectURL(file);
  pending.add();
  editor.chain().focus().insertContent([{ type: "image", attrs: { src: local, alt: file.name, pending: key } }, { type: "paragraph" }]).run();
  try {
    const up = await upload(file).catch(() => null);
    const at = positionOf(editor, key);
    if (at === null) return;
    const tr = editor.state.tr;
    if (up) tr.setNodeMarkup(at, undefined, { src: up.url, alt: up.name, attachment: up.id, pending: null });
    else tr.delete(at, at + 1);
    editor.view.dispatch(tr);
  } finally {
    URL.revokeObjectURL(local);
    pending.done();
  }
}
function positionOf(editor: Editor, key: string): number | null {
  if (editor.isDestroyed) return null;
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => { if (found === null && node.type.name === "image" && node.attrs.pending === key) found = pos; return found === null; });
  return found;
}
